import { CustomMDXEditor, FormGroupHeader } from "@/components/shared";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { Card } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { useUpdateTenderDetailsMutationHook } from "@/hooks";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { debounce } from "lodash";
import { useTenderPermissionsData } from "@/hooks/cache";

const ExecutiveSummarySchema = z.object({
  executiveSummary: z.string().optional().or(z.literal("")),
});

/**
 * ExecutiveSummaryComponent displays and manages the executive summary section of a tender
 */
function ExecutiveSummaryComponent() {
  const { t } = useTranslation(["translation"]);
  const { tenderByIdData, isTenderOwner } = useOutletContext<EditTenderContext>();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Key for forcing rerender to display the updated data in the editor
  const [key, setKey] = useState(0);

  const tenderData = tenderByIdData?.fetchTenderById;
  const tenderID = tenderData?.id;
  const { executiveSummary } = tenderData || {};

  const { canEditDetails } = useTenderPermissionsData(Number(tenderID));

  const defaultValues = useMemo(() => ({
    executiveSummary: executiveSummary || ""
  }), [executiveSummary]);

  const { updateTenderDetailsMutation, loading: isUpdatingTenderDetails } = useUpdateTenderDetailsMutationHook();

  const form = useForm<z.infer<typeof ExecutiveSummarySchema>>({
    resolver: zodResolver(ExecutiveSummarySchema),
    defaultValues
  });

  // Update form values when executiveSummary changes
  useEffect(() => {
    if (executiveSummary !== undefined) {
      form.reset({ executiveSummary: executiveSummary || "" });
      setIsLoading(false);
    }
  }, [executiveSummary, form]);

  const watchExecutiveSummary = form.watch("executiveSummary");

  // Debounced onChange handler to reduce form update frequency
  const debouncedSetValue = useCallback(
    debounce((value: string) => {
      form.setValue("executiveSummary", value);
    }, 150),
    [form]
  );

  const handleEditorChange = useCallback((value: string) => {
    debouncedSetValue(value);
  }, [debouncedSetValue]);

  /**
   * Handles saving the executive summary changes
   * @param {z.infer<typeof ExecutiveSummarySchema>} data - The form data to save
   */
  const handleSave = useCallback(async (data: z.infer<typeof ExecutiveSummarySchema>) => {
    await updateTenderDetailsMutation({
      variables: {
        tenderId: Number(tenderID),
        input: {
          executiveSummary: data.executiveSummary
        }
      }
    });
    setIsEditing(false);
    setKey(prev => prev + 1);
  }, [updateTenderDetailsMutation, tenderID]);

  /**
   * Handles canceling the edit operation and resetting form values
   */
  const handleCancel = useCallback(() => {
    form.reset(defaultValues);
    setIsEditing(false);
    setKey(prev => prev + 1);
  }, [form, defaultValues]);

  /**
   * Configuration for action buttons (edit, save, cancel)
   * Only visible to tender owners
   * @type {ActionButtonConfig[]}
   */
  const actionButtons: ActionButtonConfig[] = useMemo(() => {
    if (!isTenderOwner) return [];

    return [
      {
        text: t("common:buttons.edit"),
        variant: "outline",
        className: "",
        actionFn: () => setIsEditing(true),
        show: !isEditing && canEditDetails,
      },
      {
        text: t("common:buttons.save"),
        variant: "default",
        className: "",
        actionFn: form.handleSubmit(handleSave),
        show: isEditing,
        loading: isUpdatingTenderDetails
      },
      {
        text: t("common:buttons.cancel"),
        variant: "outline",
        className: "",
        actionFn: handleCancel,
        show: isEditing,
        disabled: isUpdatingTenderDetails
      }
    ].filter(button => button.show) as ActionButtonConfig[];
  }, [isTenderOwner, t, isEditing, form, handleSave, isUpdatingTenderDetails, handleCancel]);

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)}>
          <FormGroupHeader
            title={t("edittender.executivesummary.title")}
            actionButtons={actionButtons}
            className="pr-4 pl-2"
          />
          <div className={cn("mt-2 pr-4 pl-2", !isEditing && "pb-[300px]")}>
            <Card className={cn(!isEditing && "p-8")}>
              {isLoading ? (
                <p className="text-slate-400 text-center">
                  {t("common:loading")}
                </p>
              ) : executiveSummary || isEditing ? (
                <CustomMDXEditor
                  key={key}
                  value={watchExecutiveSummary}
                  readOnly={!isEditing}
                  overwriteDefaultClasses={!isEditing}
                  editableContentClassName={cn(isEditing && "h-[calc(100vh-16rem)]")}
                  onChange={handleEditorChange}
                />
              ) : (
                <p className="text-slate-400 text-center">
                  {t("edittender.executivesummary.nodata")}
                </p>
              )}
            </Card>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default ExecutiveSummaryComponent;
